import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ClipboardCheck,
  ChevronRight,
  Clock3,
  Factory,
  Gauge,
  ListChecks,
  Plus,
  Settings2,
  TimerReset,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { canWrite } from '@/lib/permissions';
import { useAuth } from '@/app/AuthProvider';
import {
  actualMinutes,
  hasScheduleConflict,
  plannedMinutes,
  useConfirmProductionOrder,
  useDeleteProductionSchedule,
  useProductionSchedules,
  useSaveProductionSchedule,
  useSaveWorkResource,
  useWorkResources,
  type ProductionSchedule,
  type ResourceInput,
  type ScheduleInput,
  type SchedulePriority,
  type ScheduleStatus,
  type WorkResource,
} from '@/features/manufacturing/schedules';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import type { Item } from '@/features/inventory/hooks';

const DAY_MS = 86_400_000;
const NO_RESOURCE = '__none__';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS: Record<
  ScheduleStatus,
  { label: string; variant: 'muted' | 'info-soft' | 'success-soft' | 'warning-soft' | 'outline' }
> = {
  draft: { label: 'Draft', variant: 'muted' },
  planned: { label: 'Planned', variant: 'info-soft' },
  released: { label: 'Released', variant: 'outline' },
  in_progress: { label: 'In progress', variant: 'warning-soft' },
  completed: { label: 'Completed', variant: 'success-soft' },
  blocked: { label: 'Blocked', variant: 'warning-soft' },
  cancelled: { label: 'Cancelled', variant: 'muted' },
};

function startOfDay(date: Date) {
  const out = new Date(date);
  out.setHours(0, 0, 0, 0);
  return out;
}

function startOfWeek(date: Date) {
  const out = startOfDay(date);
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
}

function addDays(date: Date, days: number) {
  return new Date(date.getTime() + days * DAY_MS);
}

function dateKey(date: Date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function localInput(iso?: string | null) {
  const date = iso ? new Date(iso) : new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDuration(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours ? `${hours}h${mins ? ` ${mins}m` : ''}` : `${mins}m`;
}

function formatRange(start: string, end: string) {
  const from = new Date(start);
  const to = new Date(end);
  const fromDate = from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const toDate = to.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fromTime = from.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const toTime = to.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return dateKey(from) === dateKey(to)
    ? `${fromDate} · ${fromTime}–${toTime}`
    : `${fromDate}, ${fromTime} – ${toDate}, ${toTime}`;
}

export function WorkCentersPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const { data: resources = [], isLoading: resourcesLoading, error: resourceError } =
    useWorkResources();
  const { data: schedules = [], isLoading: schedulesLoading, error: scheduleError } =
    useProductionSchedules();
  const [tab, setTab] = useState('board');
  const [resourceDialog, setResourceDialog] = useState<WorkResource | true | null>(null);
  const [scheduleDialog, setScheduleDialog] = useState<ProductionSchedule | true | null>(null);
  const [selectedResource, setSelectedResource] = useState('all');

  const activeSchedules = schedules.filter((s) => s.status !== 'cancelled');
  const drafts = activeSchedules.filter((s) => s.status === 'draft');
  const now = Date.now();
  const metrics = useMemo(() => {
    const confirmed = activeSchedules.filter((s) => s.status !== 'draft');
    const planned = confirmed.reduce((sum, s) => sum + plannedMinutes(s), 0);
    const completed = confirmed.filter((s) => s.status === 'completed');
    const withActuals = completed.filter((s) => actualMinutes(s) != null);
    const adherence = withActuals.length
      ? withActuals.reduce((sum, s) => {
          const actual = actualMinutes(s) ?? plannedMinutes(s);
          return sum + Math.min(plannedMinutes(s), actual) / Math.max(plannedMinutes(s), actual);
        }, 0) / withActuals.length
      : null;
    const overdue = confirmed.filter(
      (s) => s.status !== 'completed' && Date.parse(s.planned_end) < now,
    ).length;
    const weekStart = startOfWeek(new Date()).getTime();
    const weekEnd = weekStart + 7 * DAY_MS;
    const weekMinutes = confirmed
      .filter((s) => Date.parse(s.planned_start) < weekEnd && Date.parse(s.planned_end) > weekStart)
      .reduce((sum, s) => sum + plannedMinutes(s), 0);
    const capacity = resources
      .filter((r) => r.is_active)
      .reduce(
        (sum, r) =>
          sum + r.daily_capacity_hours * 60 * Math.max(r.work_center_shifts.length, 5),
        0,
      );
    return { planned, adherence, overdue, utilization: capacity ? weekMinutes / capacity : 0 };
  }, [activeSchedules, resources, now]);

  const error = resourceError ?? scheduleError;
  const loading = resourcesLoading || schedulesLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing"
        title="Work schedules"
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setResourceDialog(true)}>
                <Settings2 className="h-4 w-4" />
                Add resource
              </Button>
              <Button onClick={() => setScheduleDialog(true)} disabled={resourcesLoading}>
                <Plus className="h-4 w-4" />
                Schedule work
              </Button>
            </div>
          ) : undefined
        }
      />

      {error && (
        <div className="rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
          Work schedules could not be loaded. Apply migration 129, then refresh.{' '}
          {error instanceof Error ? error.message : ''}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <MetricCard
          label="Active resources"
          value={resources.filter((r) => r.is_active).length.toString()}
          hint={`${resources.filter((r) => r.resource_type === 'machine').length} machines · ${resources.filter((r) => r.resource_type === 'workstation').length} workstations`}
          icon={<Factory className="h-4 w-4" />}
          loading={resourcesLoading}
        />
        <MetricCard
          label="Scheduled effort"
          value={formatDuration(metrics.planned)}
          hint="Setup and run time in the current plan"
          icon={<Clock3 className="h-4 w-4" />}
          loading={schedulesLoading}
        />
        <MetricCard
          label="This week utilization"
          value={`${Math.round(metrics.utilization * 100)}%`}
          hint="Scheduled minutes ÷ available shift capacity"
          icon={<Gauge className="h-4 w-4" />}
          tone={metrics.utilization > 1 ? 'negative' : metrics.utilization > 0.8 ? 'info' : 'neutral'}
          loading={loading}
        />
        <MetricCard
          label="Schedule adherence"
          value={metrics.adherence == null ? '—' : `${Math.round(metrics.adherence * 100)}%`}
          hint={
            metrics.overdue
              ? `${metrics.overdue} overdue job${metrics.overdue === 1 ? '' : 's'}`
              : 'Planned duration compared with actual'
          }
          icon={<CheckCircle2 className="h-4 w-4" />}
          tone={metrics.overdue ? 'negative' : 'positive'}
          loading={schedulesLoading}
        />
      </div>

      {drafts.length > 0 && (
        <DraftReview drafts={drafts} canManage={canManage} onOpen={setScheduleDialog} />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList className="h-auto flex-wrap justify-start">
            <TabsTrigger value="board" className="gap-1.5">
              <Factory className="h-4 w-4" /> Machine view
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-1.5">
              <CalendarDays className="h-4 w-4" /> Calendar
            </TabsTrigger>
            <TabsTrigger value="list" className="gap-1.5">
              <ListChecks className="h-4 w-4" /> Schedule
            </TabsTrigger>
            <TabsTrigger value="resources" className="gap-1.5">
              <Wrench className="h-4 w-4" /> Resources
            </TabsTrigger>
          </TabsList>
          {tab !== 'resources' && resources.length > 1 && (
            <Select value={selectedResource} onValueChange={setSelectedResource}>
              <SelectTrigger className="h-9 w-56" aria-label="Filter by machine or workstation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All machines & workstations</SelectItem>
                {resources.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.code} · {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <TabsContent value="board">
          <MachineBoard
            resources={resources}
            schedules={activeSchedules}
            selectedResource={selectedResource}
            loading={loading}
            onOpen={setScheduleDialog}
          />
        </TabsContent>
        <TabsContent value="calendar">
          <ScheduleCalendar
            schedules={activeSchedules}
            selectedResource={selectedResource}
            loading={schedulesLoading}
            onOpen={setScheduleDialog}
          />
        </TabsContent>
        <TabsContent value="list">
          <ScheduleList
            schedules={activeSchedules}
            selectedResource={selectedResource}
            loading={schedulesLoading}
            onOpen={setScheduleDialog}
          />
        </TabsContent>
        <TabsContent value="resources">
          <ResourceGrid
            resources={resources}
            schedules={activeSchedules}
            loading={resourcesLoading}
            canManage={canManage}
            onOpen={setResourceDialog}
          />
        </TabsContent>
      </Tabs>

      {resourceDialog && (
        <ResourceDialog
          resource={resourceDialog === true ? null : resourceDialog}
          onClose={() => setResourceDialog(null)}
        />
      )}
      {scheduleDialog && (
        <ScheduleDialog
          schedule={scheduleDialog === true ? null : scheduleDialog}
          resources={resources}
          schedules={schedules}
          canManage={canManage}
          onClose={() => setScheduleDialog(null)}
        />
      )}
    </div>
  );
}

function DraftReview({
  drafts,
  canManage,
  onOpen,
}: {
  drafts: ProductionSchedule[];
  canManage: boolean;
  onOpen: (schedule: ProductionSchedule) => void;
}) {
  const confirm = useConfirmProductionOrder();
  return (
    <Card className="border-fresh-salmon/60">
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border py-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-fresh-salmon-dark" />
            Draft production orders to review
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Drafted when a made-in-house part fell to its reorder point. Drafts don't count as on
            order until they're confirmed.
          </p>
        </div>
        <Badge variant="warning-soft">{drafts.length}</Badge>
      </CardHeader>
      <CardContent className="divide-y divide-border p-0">
        {drafts.map((draft) => (
          <div
            key={draft.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{draft.title}</span>
                {draft.source === 'reorder_point' && <Badge variant="outline">Auto</Badge>}
                {draft.priority === 'high' || draft.priority === 'urgent' ? (
                  <Badge variant="warning-soft">{draft.priority === 'urgent' ? 'Urgent' : 'High'}</Badge>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {draft.item ? (
                  <Link to={`/catalog/items/${draft.item.id}`} className="hover:underline">
                    {draft.item.sku} · {draft.item.name}
                  </Link>
                ) : (
                  'No item linked'
                )}
                {' · '}
                {draft.quantity.toLocaleString()} {draft.item?.base_uom ?? 'units'}
                {' · '}
                {draft.work_center ? (
                  draft.work_center.name
                ) : (
                  <span className="text-fresh-salmon-dark">No machine assigned</span>
                )}
              </p>
              {draft.requirement && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{draft.requirement}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpen(draft)}>
                Review
              </Button>
              {canManage && (
                <Button
                  size="sm"
                  disabled={!draft.work_center_id || confirm.isPending}
                  title={draft.work_center_id ? undefined : 'Assign a machine first'}
                  onClick={() =>
                    confirm.mutate(draft.id, {
                      onSuccess: () => toast.success(`${draft.title} confirmed and scheduled.`),
                      onError: (error) =>
                        toast.error(error instanceof Error ? error.message : 'Could not confirm.'),
                    })
                  }
                >
                  Confirm
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function MachineBoard({
  resources,
  schedules,
  selectedResource,
  loading,
  onOpen,
}: {
  resources: WorkResource[];
  schedules: ProductionSchedule[];
  selectedResource: string;
  loading: boolean;
  onOpen: (schedule: ProductionSchedule) => void;
}) {
  const [week, setWeek] = useState(() => startOfWeek(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const visibleResources = resources.filter(
    (r) => selectedResource === 'all' || r.id === selectedResource,
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border py-4">
        <CardTitle>Resource loading</CardTitle>
        <WeekControls value={week} onChange={setWeek} />
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : visibleResources.length === 0 ? (
          <EmptyState
            icon={Factory}
            title="No machines or workstations"
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[980px]">
              <div className="grid grid-cols-[180px_repeat(7,minmax(110px,1fr))] border-b border-border bg-secondary/30">
                <div className="p-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Resource
                </div>
                {days.map((day) => (
                  <div key={dateKey(day)} className="border-l border-border p-3 text-center">
                    <div className="text-xs font-medium uppercase text-muted-foreground">
                      {day.toLocaleDateString(undefined, { weekday: 'short' })}
                    </div>
                    <div className={cn('text-sm', dateKey(day) === dateKey(new Date()) && 'text-info')}>
                      {day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
              {visibleResources.map((resource) => {
                const resourceSchedules = schedules.filter(
                  (s) => s.work_center_id === resource.id,
                );
                return (
                  <div
                    key={resource.id}
                    className="grid min-h-24 grid-cols-[180px_repeat(7,minmax(110px,1fr))] border-b border-border last:border-0"
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: resource.color }}
                        />
                        <span className="font-medium">{resource.name}</span>
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {resource.code} · {resource.daily_capacity_hours}h/day
                      </div>
                    </div>
                    {days.map((day) => {
                      const start = startOfDay(day).getTime();
                      const end = start + DAY_MS;
                      const jobs = resourceSchedules.filter(
                        (s) => Date.parse(s.planned_start) < end && Date.parse(s.planned_end) > start,
                      );
                      const total = jobs.reduce((sum, s) => sum + plannedMinutes(s), 0);
                      const overloaded = total > resource.daily_capacity_hours * 60;
                      return (
                        <div
                          key={dateKey(day)}
                          className={cn(
                            'space-y-1 border-l border-border p-1.5',
                            overloaded && 'bg-fresh-salmon/10',
                          )}
                        >
                          {jobs.map((job) => (
                            <button
                              key={job.id}
                              type="button"
                              onClick={() => onOpen(job)}
                              className={cn(
                                'w-full rounded-md border border-border bg-card p-1.5 text-left shadow-sm transition-colors hover:bg-secondary/40',
                                job.status === 'draft' && 'border-dashed opacity-70',
                              )}
                              style={{ borderLeft: `3px ${job.status === 'draft' ? 'dashed' : 'solid'} ${resource.color}` }}
                              title={job.status === 'draft' ? 'Draft — not confirmed yet' : undefined}
                            >
                              <span className="block truncate text-xs font-medium">{job.title}</span>
                              <span className="block text-[10px] text-muted-foreground">
                                {new Date(job.planned_start).toLocaleTimeString([], {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}{' '}
                                · {formatDuration(plannedMinutes(job))}
                              </span>
                            </button>
                          ))}
                          {overloaded && (
                            <span className="flex items-center gap-1 text-[10px] text-fresh-salmon-dark">
                              <AlertTriangle className="h-3 w-3" /> Over capacity
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WeekControls({ value, onChange }: { value: Date; onChange: (date: Date) => void }) {
  const end = addDays(value, 6);
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={() => onChange(addDays(value, -7))}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={() => onChange(startOfWeek(new Date()))}>
        Today
      </Button>
      <Button variant="ghost" size="icon" onClick={() => onChange(addDays(value, 7))}>
        <ChevronRight className="h-4 w-4" />
      </Button>
      <span className="ml-2 hidden text-sm text-muted-foreground sm:inline">
        {value.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
        {end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
      </span>
    </div>
  );
}

function ScheduleCalendar({
  schedules,
  selectedResource,
  loading,
  onOpen,
}: {
  schedules: ProductionSchedule[];
  selectedResource: string;
  loading: boolean;
  onOpen: (schedule: ProductionSchedule) => void;
}) {
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const visible = schedules.filter(
    (s) => selectedResource === 'all' || s.work_center_id === selectedResource,
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border py-4">
        <CardTitle>{month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</CardTitle>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-7 border-b border-border bg-secondary/30">
          {DAYS.map((day) => (
            <div key={day} className="p-2 text-center text-xs uppercase text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        {loading ? (
          <Skeleton className="m-6 h-96" />
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const start = startOfDay(day).getTime();
              const jobs = visible.filter(
                (s) => Date.parse(s.planned_start) >= start && Date.parse(s.planned_start) < start + DAY_MS,
              );
              return (
                <div
                  key={dateKey(day)}
                  className={cn(
                    'min-h-28 border-b border-r border-border p-1.5',
                    day.getMonth() !== month.getMonth() && 'bg-secondary/20 text-muted-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs',
                      dateKey(day) === dateKey(new Date()) && 'bg-primary text-primary-foreground',
                    )}
                  >
                    {day.getDate()}
                  </span>
                  <div className="space-y-1">
                    {jobs.slice(0, 3).map((job) => (
                      <button
                        key={job.id}
                        type="button"
                        onClick={() => onOpen(job)}
                        className="block w-full truncate rounded px-1.5 py-1 text-left text-[11px] text-foreground"
                        style={{ background: `${job.work_center?.color ?? '#557A64'}22` }}
                        title={job.title}
                      >
                        {new Date(job.planned_start).toLocaleTimeString([], {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}{' '}
                        {job.title}
                      </button>
                    ))}
                    {jobs.length > 3 && (
                      <span className="block px-1 text-[10px] text-muted-foreground">
                        +{jobs.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleList({
  schedules,
  selectedResource,
  loading,
  onOpen,
}: {
  schedules: ProductionSchedule[];
  selectedResource: string;
  loading: boolean;
  onOpen: (schedule: ProductionSchedule) => void;
}) {
  const visible = schedules.filter(
    (s) => selectedResource === 'all' || s.work_center_id === selectedResource,
  );
  if (loading) return <Skeleton className="h-80 w-full" />;
  if (!visible.length)
    return (
      <Card>
        <EmptyState
          icon={ListChecks}
          title="Nothing scheduled"
          />
      </Card>
    );
  return (
    <div className="space-y-2">
      {visible.map((schedule) => {
        const actual = actualMinutes(schedule);
        const variance = actual == null ? null : actual - plannedMinutes(schedule);
        return (
          <button
            key={schedule.id}
            type="button"
            onClick={() => onOpen(schedule)}
            className="grid w-full gap-3 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-secondary/20 md:grid-cols-[minmax(220px,1.5fr)_minmax(170px,1fr)_140px_140px]"
          >
            <div>
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ background: schedule.work_center?.color }}
                />
                <span className="font-medium">{schedule.title}</span>
                <Badge variant={STATUS[schedule.status].variant}>
                  {STATUS[schedule.status].label}
                </Badge>
                {schedule.source === 'reorder_point' && <Badge variant="outline">Auto</Badge>}
              </div>
              {schedule.item && (
                <p className="mt-1 truncate text-xs">
                  Makes {schedule.quantity.toLocaleString()} {schedule.item.base_uom} ·{' '}
                  <span className="font-mono">{schedule.item.sku}</span> {schedule.item.name}
                </p>
              )}
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {schedule.requirement || 'No requirements recorded'}
              </p>
            </div>
            <div className="text-sm">
              <span className="block">{schedule.work_center?.name ?? 'Unassigned'}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {schedule.work_center?.code}
              </span>
            </div>
            <div className="text-sm">
              <span className="block">{formatRange(schedule.planned_start, schedule.planned_end)}</span>
              <span className="text-xs text-muted-foreground">
                {formatDuration(plannedMinutes(schedule))} planned
              </span>
            </div>
            <div className="text-sm">
              {actual == null ? (
                <span className="text-muted-foreground">No actual yet</span>
              ) : (
                <>
                  <span className="block">{formatDuration(actual)} actual</span>
                  <span
                    className={cn(
                      'text-xs',
                      variance && variance > 0 ? 'text-fresh-salmon-dark' : 'text-bright-moss-dark',
                    )}
                  >
                    {variance === 0
                      ? 'On plan'
                      : `${(variance ?? 0) > 0 ? '+' : '−'}${formatDuration(Math.abs(variance ?? 0))} variance`}
                  </span>
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ResourceGrid({
  resources,
  schedules,
  loading,
  canManage,
  onOpen,
}: {
  resources: WorkResource[];
  schedules: ProductionSchedule[];
  loading: boolean;
  canManage: boolean;
  onOpen: (resource: WorkResource) => void;
}) {
  if (loading) return <Skeleton className="h-72 w-full" />;
  if (!resources.length)
    return (
      <Card>
        <EmptyState
          icon={Wrench}
          title="Build your capacity model"
          />
      </Card>
    );
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {resources.map((resource) => {
        const queued = schedules.filter(
          (s) => s.work_center_id === resource.id && s.status !== 'completed',
        );
        const queuedMinutes = queued.reduce((sum, s) => sum + plannedMinutes(s), 0);
        return (
          <Card key={resource.id} className={!resource.is_active ? 'opacity-60' : undefined}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                    style={{ background: resource.color }}
                  >
                    {resource.resource_type === 'machine' ? (
                      <Factory className="h-5 w-5" />
                    ) : (
                      <Wrench className="h-5 w-5" />
                    )}
                  </span>
                  <div>
                    <CardTitle>{resource.name}</CardTitle>
                    <p className="font-mono text-xs text-muted-foreground">{resource.code}</p>
                  </div>
                </div>
                <Badge variant={resource.is_active ? 'success-soft' : 'muted'}>
                  {resource.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <ResourceStat label="Daily capacity" value={`${resource.daily_capacity_hours}h`} />
                <ResourceStat label="Efficiency" value={`${resource.efficiency_pct}%`} />
                <ResourceStat label="Queued" value={formatDuration(queuedMinutes)} />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Standard week
                </p>
                <div className="mt-2 flex gap-1">
                  {DAYS.map((day, index) => {
                    const shift = resource.work_center_shifts.find(
                      (s) => s.day_of_week === index && s.is_active,
                    );
                    return (
                      <span
                        key={day}
                        title={shift ? `${shift.start_time.slice(0, 5)}–${shift.end_time.slice(0, 5)}` : 'Off'}
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded text-[10px]',
                          shift ? 'bg-info-soft/50 text-info' : 'bg-secondary text-muted-foreground/50',
                        )}
                      >
                        {day[0]}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
                <span>{resource.location || 'No location'} · {resource.hourly_capacity} units/hr</span>
                {canManage && (
                  <Button variant="ghost" size="sm" onClick={() => onOpen(resource)}>
                    Edit
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ResourceStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-secondary/50 p-2">
      <span className="block text-sm font-medium">{value}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

function ResourceDialog({
  resource,
  onClose,
}: {
  resource: WorkResource | null;
  onClose: () => void;
}) {
  const save = useSaveWorkResource();
  const defaultDays = resource?.work_center_shifts.map((s) => s.day_of_week) ?? [1, 2, 3, 4, 5];
  const firstShift = resource?.work_center_shifts[0];
  const [form, setForm] = useState<ResourceInput>({
    id: resource?.id,
    code: resource?.code ?? '',
    name: resource?.name ?? '',
    resource_type: resource?.resource_type ?? 'machine',
    description: resource?.description ?? '',
    location: resource?.location ?? '',
    hourly_capacity: resource?.hourly_capacity ?? 1,
    cost_per_hour_cents: resource?.cost_per_hour_cents ?? null,
    efficiency_pct: resource?.efficiency_pct ?? 100,
    daily_capacity_hours: resource?.daily_capacity_hours ?? 8,
    color: resource?.color ?? '#557A64',
    is_active: resource?.is_active ?? true,
    workingDays: defaultDays,
    shiftStart: firstShift?.start_time.slice(0, 5) ?? '08:00',
    shiftEnd: firstShift?.end_time.slice(0, 5) ?? '16:00',
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return;
    save.mutate(
      { ...form, code: form.code.trim().toUpperCase(), name: form.name.trim() },
      {
        onSuccess: () => {
          toast.success(resource ? 'Resource updated.' : 'Resource added.');
          onClose();
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save resource.'),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{resource ? 'Edit resource' : 'Add machine or workstation'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Resource code">
              <Input
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="CNC-01"
              />
            </Field>
            <Field label="Name">
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="CNC Mill 1"
              />
            </Field>
            <Field label="Type">
              <Select
                value={form.resource_type}
                onValueChange={(value: 'machine' | 'workstation') =>
                  setForm({ ...form, resource_type: value })
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="machine">Machine</SelectItem>
                  <SelectItem value="workstation">Workstation</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Location">
              <Input
                value={form.location ?? ''}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Building A · Bay 2"
              />
            </Field>
            <Field label="Units per hour">
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={form.hourly_capacity}
                onChange={(e) => setForm({ ...form, hourly_capacity: Number(e.target.value) })}
              />
            </Field>
            <Field label="Efficiency %">
              <Input
                type="number"
                min="1"
                max="200"
                value={form.efficiency_pct}
                onChange={(e) => setForm({ ...form, efficiency_pct: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div>
            <Label>Working days</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((day, index) => (
                <button
                  type="button"
                  key={day}
                  onClick={() =>
                    setForm({
                      ...form,
                      workingDays: form.workingDays.includes(index)
                        ? form.workingDays.filter((d) => d !== index)
                        : [...form.workingDays, index],
                    })
                  }
                  className={cn(
                    'h-9 rounded-md border px-3 text-sm',
                    form.workingDays.includes(index)
                      ? 'border-info bg-info-soft/30 text-info'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Shift starts">
              <Input
                type="time"
                value={form.shiftStart}
                onChange={(e) => setForm({ ...form, shiftStart: e.target.value })}
              />
            </Field>
            <Field label="Shift ends">
              <Input
                type="time"
                value={form.shiftEnd}
                onChange={(e) => setForm({ ...form, shiftEnd: e.target.value })}
              />
            </Field>
            <Field label="Daily capacity">
              <Input
                type="number"
                min="0.25"
                max="24"
                step="0.25"
                value={form.daily_capacity_hours}
                onChange={(e) => setForm({ ...form, daily_capacity_hours: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-5 rounded-lg bg-secondary/40 p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-8 w-10 rounded border-0 bg-transparent"
              />
              Schedule color
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Available for scheduling
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending || !form.workingDays.length}>
              {save.isPending ? 'Saving…' : 'Save resource'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({
  schedule,
  resources,
  schedules,
  canManage,
  onClose,
}: {
  schedule: ProductionSchedule | null;
  resources: WorkResource[];
  schedules: ProductionSchedule[];
  canManage: boolean;
  onClose: () => void;
}) {
  const save = useSaveProductionSchedule();
  const remove = useDeleteProductionSchedule();
  const initialStart = schedule?.planned_start ?? new Date(Date.now() + 60 * 60_000).toISOString();
  const initialEnd = schedule?.planned_end ?? new Date(Date.parse(initialStart) + 2 * 60 * 60_000).toISOString();
  const [form, setForm] = useState<ScheduleInput>({
    id: schedule?.id,
    work_center_id: schedule
      ? schedule.work_center_id
      : (resources.find((r) => r.is_active)?.id ?? null),
    work_order_id: schedule?.work_order_id ?? null,
    item_id: schedule?.item_id ?? null,
    title: schedule?.title ?? '',
    requirement: schedule?.requirement ?? '',
    quantity: schedule?.quantity ?? 1,
    setup_minutes: schedule?.setup_minutes ?? 0,
    run_minutes: schedule?.run_minutes ?? 120,
    planned_start: localInput(initialStart),
    planned_end: localInput(initialEnd),
    actual_start: schedule?.actual_start ? localInput(schedule.actual_start) : null,
    actual_end: schedule?.actual_end ? localInput(schedule.actual_end) : null,
    actual_quantity: schedule?.actual_quantity ?? null,
    status: schedule?.status ?? (resources.some((r) => r.is_active) ? 'planned' : 'draft'),
    priority: schedule?.priority ?? 'normal',
    notes: schedule?.notes ?? '',
  });
  const conflict = hasScheduleConflict(
    {
      ...form,
      planned_start: form.planned_start,
      planned_end: form.planned_end,
    },
    schedules,
  );
  const planned = form.setup_minutes + form.run_minutes;
  const plannedSpan = Date.parse(form.planned_end) - Date.parse(form.planned_start);
  const calendarDays = Number.isFinite(plannedSpan)
    ? Math.max(1, Math.ceil(plannedSpan / DAY_MS))
    : 1;
  const actual =
    form.actual_start && form.actual_end
      ? Math.max(0, Math.round((Date.parse(form.actual_end) - Date.parse(form.actual_start)) / 60_000))
      : null;

  const [pickingItem, setPickingItem] = useState(!schedule?.item_id);
  const [pickedItem, setPickedItem] = useState<Pick<Item, 'id' | 'sku' | 'name' | 'base_uom'> | null>(
    schedule?.item ?? null,
  );

  const submit = (event: Pick<FormEvent, 'preventDefault'>, statusOverride?: ScheduleStatus) => {
    event.preventDefault();
    const status = statusOverride ?? form.status;
    if (!canManage || !form.title.trim()) return;
    if (!form.work_center_id && status !== 'draft' && status !== 'cancelled') {
      toast.error('Pick a machine or workstation before scheduling this order.');
      return;
    }
    if (Date.parse(form.planned_end) <= Date.parse(form.planned_start)) {
      toast.error('Planned end must be after the planned start.');
      return;
    }
    if (form.actual_end && !form.actual_start) {
      toast.error('Enter an actual start before the actual end.');
      return;
    }
    save.mutate(
      {
        ...form,
        status,
        title: form.title.trim(),
        planned_start: new Date(form.planned_start).toISOString(),
        planned_end: new Date(form.planned_end).toISOString(),
        actual_start: form.actual_start ? new Date(form.actual_start).toISOString() : null,
        actual_end: form.actual_end ? new Date(form.actual_end).toISOString() : null,
      },
      {
        onSuccess: () => {
          toast.success(
            statusOverride === 'planned'
              ? 'Production order confirmed and scheduled.'
              : schedule
                ? 'Schedule updated.'
                : 'Work scheduled.',
          );
          onClose();
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save schedule.'),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{schedule ? schedule.title : 'New production order'}</DialogTitle>
          {schedule?.status === 'draft' && (
            <p className="text-sm text-muted-foreground">
              {schedule.source === 'reorder_point'
                ? 'Drafted automatically at the reorder point. Check the machine, dates and quantity, then confirm to put it on the schedule.'
                : 'Draft — confirm to put it on the schedule.'}
            </p>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Job or operation" className="sm:col-span-2">
              <Input
                required
                disabled={!canManage}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Cut frame rails · WO-1042"
              />
            </Field>
            <div className="sm:col-span-2">
              {pickingItem && canManage ? (
                <ItemPicker
                  label="Item produced"
                  value={form.item_id ?? null}
                  placeholder="Link the part this order makes so it counts as on order…"
                  filter={(it) => it.is_active}
                  onChange={(picked) => {
                    if (!picked) return;
                    setPickedItem(picked);
                    setPickingItem(false);
                    setForm({
                      ...form,
                      item_id: picked.id,
                      title: form.title.trim() ? form.title : picked.name,
                      work_center_id:
                        !schedule && picked.default_work_center_id
                          ? picked.default_work_center_id
                          : form.work_center_id,
                    });
                  }}
                />
              ) : (
                <>
                  <Label className="mb-1.5 block">Item produced</Label>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                    {pickedItem ? (
                      <span className="truncate">
                        <span className="font-mono text-xs text-muted-foreground">{pickedItem.sku}</span>{' '}
                        {pickedItem.name}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No item linked</span>
                    )}
                    {canManage && (
                      <div className="flex shrink-0 gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setPickingItem(true)}>
                          Change
                        </Button>
                        {pickedItem && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPickedItem(null);
                              setForm({ ...form, item_id: null });
                            }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Open orders for a linked item show in its On order and Arrival date (the planned end)
                until they're completed.
              </p>
            </div>
            <Field label="Machine / workstation">
              <Select
                disabled={!canManage}
                value={form.work_center_id ?? NO_RESOURCE}
                onValueChange={(value) =>
                  setForm({ ...form, work_center_id: value === NO_RESOURCE ? null : value })
                }
              >
                <SelectTrigger><SelectValue placeholder="Select resource" /></SelectTrigger>
                <SelectContent>
                  {(form.status === 'draft' || !form.work_center_id) && (
                    <SelectItem value={NO_RESOURCE}>Not assigned yet</SelectItem>
                  )}
                  {resources.filter((r) => r.is_active || r.id === form.work_center_id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.code} · {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select
                disabled={!canManage}
                value={form.priority}
                onValueChange={(value: SchedulePriority) => setForm({ ...form, priority: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Requirements" className="sm:col-span-2">
              <textarea
                disabled={!canManage}
                value={form.requirement ?? ''}
                onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                placeholder="Material, tooling, inspection, operator skill, or preceding operation needed…"
                className="min-h-20 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              />
            </Field>
          </div>

          <div className="rounded-lg border border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
              <CalendarDays className="h-4 w-4 text-info" /> Planned baseline
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Starts">
                <Input
                  type="datetime-local"
                  disabled={!canManage}
                  value={form.planned_start}
                  onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                />
              </Field>
              <Field label="Ends">
                <Input
                  type="datetime-local"
                  disabled={!canManage}
                  value={form.planned_end}
                  onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
                />
              </Field>
              <Field label="Setup minutes">
                <Input
                  type="number"
                  min="0"
                  disabled={!canManage}
                  value={form.setup_minutes}
                  onChange={(e) => setForm({ ...form, setup_minutes: Number(e.target.value) })}
                />
              </Field>
              <Field label="Run minutes">
                <Input
                  type="number"
                  min="1"
                  disabled={!canManage}
                  value={form.run_minutes}
                  onChange={(e) => setForm({ ...form, run_minutes: Number(e.target.value) })}
                />
              </Field>
              <Field label="Required quantity">
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  disabled={!canManage}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
                />
              </Field>
              <div className="flex items-end pb-2 text-sm text-muted-foreground">
                <Clock3 className="mr-2 h-4 w-4" /> {formatDuration(planned)} effort ·{' '}
                {calendarDays} calendar day{calendarDays === 1 ? '' : 's'}
              </div>
            </div>
            {conflict && (
              <p className="mt-3 flex items-center gap-2 rounded-md bg-fresh-salmon/15 px-3 py-2 text-xs text-fresh-salmon-dark">
                <AlertTriangle className="h-4 w-4" />
                This resource already has work scheduled during part of this window.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
              <TimerReset className="h-4 w-4 text-info" /> Production reality
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Status">
                <Select
                  disabled={!canManage}
                  value={form.status}
                  onValueChange={(value: ScheduleStatus) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([value, meta]) => (
                      <SelectItem key={value} value={value}>{meta.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Actual quantity">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={!canManage}
                  value={form.actual_quantity ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, actual_quantity: e.target.value ? Number(e.target.value) : null })
                  }
                />
              </Field>
              <Field label="Actual start">
                <Input
                  type="datetime-local"
                  disabled={!canManage}
                  value={form.actual_start ?? ''}
                  onChange={(e) => setForm({ ...form, actual_start: e.target.value || null })}
                />
              </Field>
              <Field label="Actual end">
                <Input
                  type="datetime-local"
                  disabled={!canManage}
                  value={form.actual_end ?? ''}
                  onChange={(e) => setForm({ ...form, actual_end: e.target.value || null })}
                />
              </Field>
            </div>
            {actual != null && (
              <div className="mt-3 flex flex-wrap gap-3 text-xs">
                <Badge variant="outline">{formatDuration(actual)} actual</Badge>
                <Badge variant={actual <= planned ? 'success-soft' : 'warning-soft'}>
                  {actual === planned
                    ? 'On plan'
                    : `${actual > planned ? '+' : '−'}${formatDuration(Math.abs(actual - planned))} vs plan`}
                </Badge>
                {form.actual_quantity != null && (
                  <Badge variant={form.actual_quantity >= form.quantity ? 'success-soft' : 'warning-soft'}>
                    {Math.round((form.actual_quantity / form.quantity) * 100)}% quantity complete
                  </Badge>
                )}
              </div>
            )}
          </div>

          <Field label="Notes">
            <textarea
              disabled={!canManage}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="min-h-16 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              placeholder="Planner notes, handoff details, or the reason for variance…"
            />
          </Field>

          <DialogFooter className="sm:justify-between">
            <div>
              {schedule && canManage && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-fresh-salmon-dark"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (!window.confirm('Delete this scheduled job?')) return;
                    remove.mutate(schedule.id, {
                      onSuccess: () => {
                        toast.success('Scheduled job deleted.');
                        onClose();
                      },
                      onError: (error) =>
                        toast.error(error instanceof Error ? error.message : 'Could not delete job.'),
                    });
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {canManage ? 'Cancel' : 'Close'}
              </Button>
              {canManage && form.status === 'draft' && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={save.isPending || !form.title.trim()}
                  onClick={(e) => submit(e, 'draft')}
                >
                  Save draft
                </Button>
              )}
              {canManage && form.status === 'draft' ? (
                <Button
                  type="button"
                  disabled={save.isPending || !form.title.trim() || !form.work_center_id}
                  title={form.work_center_id ? undefined : 'Assign a machine first'}
                  onClick={(e) => submit(e, 'planned')}
                >
                  {save.isPending ? 'Saving…' : 'Confirm & schedule'}
                </Button>
              ) : (
                canManage && (
                  <Button type="submit" disabled={save.isPending || !form.title.trim()}>
                    {save.isPending ? 'Saving…' : schedule ? 'Save changes' : 'Schedule work'}
                  </Button>
                )
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
